/**
 * BarChart — paired with LineChart in style and tokens. Used by the
 * "Invitations created" widget where bars communicate discrete counts more
 * clearly than an area line.
 */

import * as React from 'react'
import type { TimeSeriesPoint } from '../types'

interface Props {
  data: TimeSeriesPoint[]
  height?: number
  accentClassName?: string
  formatValue?: (value: number) => string
  formatLabel?: (bucket: string) => string
  ariaLabel?: string
}

const PADDING = { top: 16, right: 16, bottom: 28, left: 44 }

export function BarChart({
  data,
  height = 220,
  accentClassName = 'text-primary',
  formatValue,
  formatLabel,
  ariaLabel = 'Trend bar chart',
}: Props) {
  const width = 600
  const fmtV = formatValue ?? ((v) => v.toLocaleString())
  const fmtL = formatLabel ?? defaultLabelFormatter

  const max = Math.max(1, ...data.map((p) => p.value))
  const yMax = niceCeil(max)

  const chartW = width - PADDING.left - PADDING.right
  const chartH = height - PADDING.top - PADDING.bottom
  const barGap = data.length > 30 ? 1 : 4
  const barW = data.length > 0 ? Math.max(2, chartW / data.length - barGap) : 0

  const yTicks = [0, 0.5, 1].map((t) => ({
    y: PADDING.top + chartH - t * chartH,
    label: fmtV(Math.round(yMax * t)),
  }))

  const labelStride = Math.max(1, Math.ceil(data.length / 6))

  if (data.length === 0) {
    return (
      <div
        className="w-full flex items-center justify-center text-on-surface-variant text-sm"
        style={{ height }}
      >
        No data yet for this range.
      </div>
    )
  }

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="w-full block"
      style={{ height }}
    >
      {yTicks.map((t, idx) => (
        <g key={idx}>
          <line
            x1={PADDING.left}
            x2={width - PADDING.right}
            y1={t.y}
            y2={t.y}
            stroke="currentColor"
            strokeOpacity={0.08}
            strokeDasharray={idx === 0 ? '0' : '3 3'}
            className="text-on-surface"
          />
          <text
            x={PADDING.left - 8}
            y={t.y + 3}
            textAnchor="end"
            fontSize={10}
            className="fill-on-surface-variant"
          >
            {t.label}
          </text>
        </g>
      ))}

      {data.map((p, i) => {
        const h = (p.value / yMax) * chartH
        const bx = PADDING.left + i * (chartW / data.length) + barGap / 2
        const by = PADDING.top + chartH - h
        return (
          <rect
            key={i}
            x={bx}
            y={by}
            width={barW}
            height={Math.max(0, h)}
            rx={2}
            className={accentClassName}
            fill="currentColor"
            opacity={0.85}
          >
            <title>{`${fmtL(p.bucket)}: ${fmtV(p.value)}`}</title>
          </rect>
        )
      })}

      {data
        .map((p, i) => ({ i, p }))
        .filter(({ i }) => i % labelStride === 0 || i === data.length - 1)
        .map(({ i, p }) => (
          <text
            key={i}
            x={PADDING.left + i * (chartW / data.length) + barW / 2}
            y={height - 8}
            textAnchor="middle"
            fontSize={10}
            className="fill-on-surface-variant"
          >
            {fmtL(p.bucket)}
          </text>
        ))}
    </svg>
  )
}

function niceCeil(v: number): number {
  if (v <= 1) return 1
  const pow = Math.pow(10, Math.floor(Math.log10(v)))
  const norm = v / pow
  const steps = [1, 2, 2.5, 5, 10]
  const step = steps.find((s) => norm <= s) ?? 10
  return step * pow
}

function defaultLabelFormatter(bucket: string): string {
  if (bucket.length === 7) {
    const d = new Date(`${bucket}-01T00:00:00Z`)
    return d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
  }
  const d = new Date(`${bucket}T00:00:00Z`)
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}
